import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  advancedFilter,
  Conditions,
  FilterField,
  GET_PAYROLL_QRE_SUMMARY,
  GetPayrollQreSummaryQuery,
  GetPayrollQreSummaryQueryVariables,
  keywordFilter,
  PayrollQreSummaryFilter,
  useSticky,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

export type PayrollQreSummaryFilterableColumn = FilterField<PayrollQreSummaryFilter>;

export type PayrollQreSummary = NonNullable<GetPayrollQreSummaryQuery['getPayrollQreSummary']>;

type UsePayrollQreSummaryProps = {
  filterConditions?: Conditions<PayrollQreSummaryFilterableColumn>;
  searchKeyword?: string | null;
};

type UsePayrollQreSummaryResult =
  | {
      loading: true;
      payrollQreSummary: null;
    }
  | {
      loading: false;
      payrollQreSummary: PayrollQreSummary;
    };

export const usePayrollQreSummary = ({
  filterConditions,
  searchKeyword,
}: UsePayrollQreSummaryProps = {}): UsePayrollQreSummaryResult => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetPayrollQreSummaryQuery, GetPayrollQreSummaryQueryVariables>(GET_PAYROLL_QRE_SUMMARY, {
    variables: {
      fiscalYearId,
      filter: {
        and: [
          keywordFilter(['name', 'title'], searchKeyword),
          advancedFilter<PayrollQreSummaryFilter>(filterConditions),
        ],
      },
    },
  });

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const payrollQreSummary = stickyData?.getPayrollQreSummary ?? null;

  if (loading) {
    return {
      loading: true,
      payrollQreSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !payrollQreSummary) {
    throw createError('MissingPayrollQreSummary', 'No data returned from getPayrollQreSummary query');
  }

  return {
    loading,
    payrollQreSummary,
  };
};
